import { iconText } from './iconText.js';
// Mancala renderer on generated art: mancala_board.webp + 4 stone sprite
// variants (mancala_stone_0..3) scattered in the carved pits, with a numeric
// count per pit (readability first). Pure view — calls back `onMove(pitIndex)`
// with the ABSOLUTE index into state.pits (0-5 = 'w' pits, 6 = 'w' store,
// 7-12 = 'b' pits, 13 = 'b' store).
//
// Orientation: MY pits sit on the BOTTOM row in sow order left->right with MY
// store on the RIGHT; the opponent's row runs right->left on top (CCW visual
// continuity) with their store on the LEFT. Spectators get the 'w'-bottom view.
//
// Extra turn (Kalah): a new state whose turn is STILL mine right after my own
// move shows "Masz dodatkowy ruch! 🔁" above the board.

// Measured geometry of mancala_board.webp (1709×618 source; gold-rim scan
// 2026-07-11 — the first hollow-darkness trace undershot the pit HEIGHT, the
// bowls' lit lower halves didn't pass the threshold): pit centres, radii and
// store boxes as fractions of the source edges — display coordinates are
// these times the drawn board size. Full pit ovals: y 55–277 (top row) and
// 336–559 (bottom row); stores y 55–563.
const SRC_W = 1709, SRC_H = 618;
const PIT_XS = [340, 540, 742, 944, 1148, 1352].map((v) => v / SRC_W);
const TOP_Y = 166 / SRC_H, BOT_Y = 448 / SRC_H;
const PIT_RX = 84 / SRC_W, PIT_RY = 111 / SRC_H;
const STORE_X = { left: 134 / SRC_W, right: 1566 / SRC_W };
const STORE_CY = 309 / SRC_H;
const STORE_RX = 86 / SRC_W, STORE_RY = 254 / SRC_H;
// The scene hands us a square `size`; the flat board reads better a bit wider.
const BOARD_W_FRAC = 1.26;

const STONE_TEXTURES = ['mancala_stone_0', 'mancala_stone_1', 'mancala_stone_2', 'mancala_stone_3'];
const PIT_LIT = 0x3ad07a;        // legal-move highlight ring
const TXT = { fontFamily: 'system-ui', fontSize: '18px', color: '#f5eecb', fontStyle: 'bold' };

// Deterministic per-(slot, ordinal) pseudo-random in [0,1): stones must not
// reshuffle between redraws of the same state, so no Math.random anywhere.
function jitter(slot, ordinal, salt) {
  const n = Math.sin(slot * 127.1 + ordinal * 311.7 + salt * 74.7) * 43758.5453;
  return n - Math.floor(n);
}

export function createMancalaRenderer(scene, { x, y, size, myColor, onMove }) {
  const BW = size * BOARD_W_FRAC, BH = BW * (SRC_H / SRC_W);
  const left = x - BW / 2, top = y - BH / 2;
  const pitX = (k) => left + PIT_XS[k] * BW;
  const rowY = { top: top + TOP_Y * BH, bottom: top + BOT_Y * BH };
  const rx = PIT_RX * BW, ry = PIT_RY * BH;
  const storeX = { left: left + STORE_X.left * BW, right: left + STORE_X.right * BW };
  const storeCY = top + STORE_CY * BH;
  const srx = STORE_RX * BW, sry = STORE_RY * BH;
  const stoneSize = ry * 0.52;

  let color = myColor ?? null;
  let flip = myColor === 'b';           // flip: 'b' pits on the bottom row
  let pits = new Array(14).fill(0);
  let legalMoves = [];
  let interactive = false;
  let prevTurn = null;                  // turn BEFORE the latest state (extra-turn banner)
  let showExtra = false;

  // display column k (0..5, left->right) -> absolute pit index.
  const bottomPit = (k) => (flip ? 7 + k : k);
  const topPit = (k) => (flip ? 5 - k : 12 - k);
  const myStoreIdx = () => (flip ? 13 : 6);
  const oppStoreIdx = () => (flip ? 6 : 13);

  const container = scene.add.container(0, 0).setDepth(10);
  const boardImg = scene.add.image(x, y, 'mancala_board').setDisplaySize(BW, BH);
  const highlights = scene.add.graphics();
  const seedLayer = scene.add.container(0, 0);
  const banner = iconText(scene, x, top - 26, 'Masz dodatkowy ruch! {repeat}', {
    fontFamily: 'system-ui', fontSize: '22px', color: '#ffd166', fontStyle: 'bold',
  }).setOrigin(0.5).setVisible(false);
  container.add([boardImg, highlights, seedLayer, banner]);

  // One zone per bottom-row pit (only own pits are ever tappable). pointerUP —
  // the hall-wide idiom (see chessRenderer.js) so taps can't leak into modals.
  const zones = [];
  for (let k = 0; k < 6; k++) {
    const z = scene.add.zone(pitX(k), rowY.bottom, rx * 2.3, ry * 2.3)
      .setInteractive({ useHandCursor: true });
    z.on('pointerup', () => {
      const idx = bottomPit(k);
      if (interactive && legalMoves.includes(idx)) onMove(idx);
    });
    zones.push(z);
    container.add(z);
  }

  // --- stone colour identity -------------------------------------------------
  // The physical set is 12 stones of each of the 4 variants (48 = the engine's
  // conserved total). stoneColors[slot] lists the variant of every stone lying
  // in that slot; on each state change we infer the move by replaying each
  // legal sow (a JS port of the engine's sow/capture/bank) against the new
  // counts and route the actual stones accordingly — colours are conserved for
  // the whole match. Unknown transitions (reconnect/spectate mid-game) rebuild
  // from a deterministically shuffled full pool, still 12 per colour.
  let stoneColors = null;   // Array(14) of arrays of texture indices
  let prevPits = null;

  function seededShuffle(arr, seed) {
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(jitter(seed, i, 3) * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  }

  function rebuildColors(p) {
    const pool = [];
    for (let v = 0; v < STONE_TEXTURES.length; v++) {
      for (let n = 0; n < 12; n++) pool.push(v);
    }
    seededShuffle(pool, 7);
    stoneColors = Array.from({ length: 14 }, () => []);
    for (let s = 0; s < 14; s++) {
      for (let n = 0; n < p[s]; n++) stoneColors[s].push(pool.pop() ?? (s + n) % 4);
    }
  }

  const ownOf = (c) => (c === 'w' ? [0, 1, 2, 3, 4, 5] : [7, 8, 9, 10, 11, 12]);
  const storeOf = (c) => (c === 'w' ? 6 : 13);

  // JS port of the engine's apply_move (sow CCW skipping the opponent store,
  // Kalah capture, endgame bank) that also records which slots received the
  // sown stones, in order. Must mirror backend/app/game/engines/mancala.py.
  function simulateMove(prev, me, m) {
    const pits = prev.slice();
    const opp = me === 'w' ? 'b' : 'w';
    const order = [];
    let seeds = pits[m]; pits[m] = 0; let i = m;
    while (seeds > 0) {
      i = (i + 1) % 14;
      if (i === storeOf(opp)) continue;
      pits[i] += 1; seeds -= 1; order.push(i);
    }
    const turn = i === storeOf(me) ? me : opp;
    let capture = null;
    if (ownOf(me).includes(i) && pits[i] === 1 && pits[12 - i] > 0) {
      capture = [i, 12 - i];
      pits[storeOf(me)] += pits[12 - i] + 1; pits[i] = 0; pits[12 - i] = 0;
    }
    let bank = null;
    if (ownOf(turn).every((j) => pits[j] === 0)) {
      const other = turn === 'w' ? 'b' : 'w';
      bank = other;
      pits[storeOf(other)] += ownOf(other).reduce((a, j) => a + pits[j], 0);
      for (const j of ownOf(other)) pits[j] = 0;
    }
    return { pits, order, capture, bank };
  }

  function advanceColors(prevP, nextP, mover) {
    if (!stoneColors || !mover) { rebuildColors(nextP); return; }
    for (const m of ownOf(mover)) {
      if (prevP[m] === 0) continue;
      const sim = simulateMove(prevP, mover, m);
      if (!sim.pits.every((v, idx) => v === nextP[idx])) continue;
      const moving = stoneColors[m].splice(0);
      for (const slot of sim.order) stoneColors[slot].push(moving.shift());
      if (sim.capture) {
        const [at, across] = sim.capture;
        stoneColors[storeOf(mover)].push(...stoneColors[at].splice(0), ...stoneColors[across].splice(0));
      }
      if (sim.bank) {
        for (const j of ownOf(sim.bank)) stoneColors[storeOf(sim.bank)].push(...stoneColors[j].splice(0));
      }
      return;
    }
    rebuildColors(nextP);   // no legal sow reproduces the diff (resume/desync)
  }

  // The jitter box is rectangular but the bowls are not — pull any offset that
  // falls outside the bowl back onto it, or stones end up on the rims/corners.
  // exp 2 = ellipse (pits); exp 4 = squircle (the rounded store troughs).
  function clampToBowl(ox, oy, sx, sy, exp) {
    const t = Math.abs(ox / sx) ** exp + Math.abs(oy / sy) ** exp;
    if (t <= 1) return [ox, oy];
    const k = 0.92 * t ** (-1 / exp);
    return [ox * k, oy * k];
  }

  // Scatter is deterministic per (slot, ordinal); the variant comes from the
  // tracked identity above (fallback keeps a stable pattern if tracking is off).
  function addStone(slot, d, cx, cy, spreadX, spreadY, exp) {
    const tex = STONE_TEXTURES[stoneColors?.[slot]?.[d] ?? (slot * 5 + d * 3) % STONE_TEXTURES.length];
    const [ox, oy] = clampToBowl(
      (jitter(slot, d, 1) - 0.5) * 2 * spreadX,
      (jitter(slot, d, 2) - 0.5) * 2 * spreadY,
      spreadX, spreadY, exp);
    // Soft contact shadow under the stone (light from above) so it doesn't
    // read flat against the bowl; added before the sprite = drawn beneath it.
    seedLayer.add(scene.add.ellipse(cx + ox, cy + oy + stoneSize * 0.30,
      stoneSize * 0.9, stoneSize * 0.42, 0x000000, 0.30));
    seedLayer.add(scene.add.image(cx + ox, cy + oy, tex).setDisplaySize(stoneSize, stoneSize));
  }

  // countAbove: the top row and the left store label ABOVE the bowl (they read
  // toward the opponent's edge), the bottom row and right store below it.
  function drawPit(slot, cx, cy, count, lit, countAbove) {
    if (lit) {
      // wider than the measured rim on purpose — the highlight should wrap the
      // carved oval including its gold edge, not just the hollow (user-tuned).
      highlights.fillStyle(PIT_LIT, 0.07).fillEllipse(cx, cy, rx * 2.1, ry * 2.05);
      highlights.lineStyle(2, PIT_LIT, 0.5).strokeEllipse(cx, cy, rx * 2.1, ry * 2.05);
    }
    const shown = Math.min(count, 8);   // the number carries exact information
    for (let d = 0; d < shown; d++) addStone(slot, d, cx, cy, rx - stoneSize / 2, ry - stoneSize / 2, 2);
    seedLayer.add(iconText(scene, cx, countAbove ? cy - ry - 7 : cy + ry + 13, `${count}`, TXT).setOrigin(0.5));
  }

  function drawStore(slot, cx, count, countAbove) {
    const shown = Math.min(count, 12);
    for (let d = 0; d < shown; d++) addStone(slot, d, cx, storeCY, srx - stoneSize / 2, sry - stoneSize / 2, 4);
    seedLayer.add(iconText(scene, cx, countAbove ? storeCY - sry - 8 : storeCY + sry + 16, `${count}`, TXT).setOrigin(0.5));
  }

  function redraw() {
    highlights.clear();
    seedLayer.removeAll(true);
    for (let k = 0; k < 6; k++) {
      const bi = bottomPit(k);
      drawPit(bi, pitX(k), rowY.bottom, pits[bi], interactive && legalMoves.includes(bi), false);
      drawPit(topPit(k), pitX(k), rowY.top, pits[topPit(k)], false, true);
    }
    drawStore(myStoreIdx(), storeX.right, pits[myStoreIdx()], false);
    drawStore(oppStoreIdx(), storeX.left, pits[oppStoreIdx()], true);
    banner.setVisible(showExtra);
  }

  function update({ legalMoves: lm, interactive: on, myColor: mc, state: st }) {
    if (mc !== undefined) { color = mc ?? null; flip = mc === 'b'; }
    const newTurn = st?.turn ?? null;
    // Banner: my move just granted me another one (prev turn mine, still mine).
    showExtra = !!(on && color && prevTurn === color && newTurn === color);
    if (st?.pits) {
      const changed = !prevPits || st.pits.some((v, idx) => v !== prevPits[idx]);
      if (!stoneColors) rebuildColors(st.pits);
      else if (changed) advanceColors(prevPits, st.pits, prevTurn);
      prevPits = st.pits.slice();
      pits = st.pits.slice(); prevTurn = newTurn;
    }
    legalMoves = lm || [];
    interactive = !!on;
    redraw();
  }

  // Panel hook: mirror each side's STORE count next to the player panels
  // (the generic captured counter is meaningless for mancala).
  function capturedGlyphs(st, panelColor) {
    if (!st?.pits) return null;
    return `Spichlerz: ${st.pits[panelColor === 'w' ? 6 : 13]} ●`;
  }

  // Sfx hook: sowing only ever ADDS to the opponent's pits — their row total
  // dropping means the mover captured. (state has no board array, so the
  // scene's generic count heuristic cannot see mancala captures.)
  function moveSfx(prevState, nextState) {
    if (!prevState?.pits || !nextState?.pits) return 'mancala_sow';
    const side = prevState.turn === 'w' ? [7, 8, 9, 10, 11, 12] : [0, 1, 2, 3, 4, 5];
    const sum = (p) => side.reduce((a, i) => a + p[i], 0);
    return sum(nextState.pits) < sum(prevState.pits) ? 'mancala_capture' : 'mancala_sow';
  }

  function clearSelection() {}   // taps are single-step; nothing to clear

  function destroy() {
    zones.forEach((z) => z.destroy());
    container.destroy(true);
  }

  return {
    update, clearSelection, destroy, capturedGlyphs, moveSfx,
    // e2e hook: drive the exact tap->onMove path without a real pointer.
    tapPit: (i) => { if (interactive && legalMoves.includes(i)) onMove(i); },
  };
}
